import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { ThemeProvider } from '../context/ThemeContext';
import {
  Award, AlertTriangle, Loader2, BarChart3, MessageSquare,
  Target, TrendingUp, XCircle, Zap, ChevronDown,
  Shield, Clock, Eye, Share2, Copy, Ban, Terminal
} from 'lucide-react';
import { getSharedResults } from '../services/api';

const ScoreRing = ({ score, size = 100 }) => {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#22c55e' : score >= 65 ? '#3b82f6' : score >= 50 ? '#eab308' : '#ef4444';

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="currentColor" strokeWidth="6" className="text-muted/20" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-3xl font-bold" style={{ color }}>{Math.round(score)}</span>
        <span className="text-[10px] text-muted-foreground">/ 100</span>
      </div>
    </div>
  );
};

const ScoreBar = ({ label, score, icon: Icon }) => {
  const color = score >= 80 ? 'bg-green-500' : score >= 65 ? 'bg-blue-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          {Icon && <Icon className="h-3.5 w-3.5" />} {label}
        </span>
        <span className="font-semibold">{Math.round(score)}</span>
      </div>
      <Progress value={score} className={`h-2 [&>div]:${color}`} />
    </div>
  );
};

const QuestionCard = ({ q, index }) => {
  const [open, setOpen] = useState(false);
  const s = q.score || 0;
  const scoreColor = s >= 80 ? 'text-green-500' : s >= 65 ? 'text-blue-500' : s >= 50 ? 'text-yellow-500' : 'text-red-500';

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="w-full text-left p-3 rounded-lg border hover:bg-muted/30 transition-colors">
        <div className="flex items-center gap-3">
          <div className={`text-lg font-bold w-8 text-center ${scoreColor}`}>{Math.round(s)}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium break-words">Q{index + 1}: {q.question}</p>
            <div className="flex items-center gap-2 mt-0.5">
              {q.category && <Badge variant="secondary" className="text-[10px] h-4">{q.category}</Badge>}
              {q.communication_quality && <span className="text-[10px] text-muted-foreground">Comm: {q.communication_quality}</span>}
            </div>
          </div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 pb-3 space-y-2 mt-2">
          {q.feedback && <p className="text-sm text-muted-foreground">{q.feedback}</p>}
          {q.key_points?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {q.key_points.map((p, i) => (
                <Badge key={i} variant="outline" className="text-[10px]">{p}</Badge>
              ))}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default function SharedResultsPage() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await getSharedResults(shareToken);
        setData(result);
      } catch (e) {
        const status = e.response?.status;
        if (status === 410) setError('This shared link has expired.');
        else if (status === 404) setError('Shared link not found.');
        else setError('Failed to load interview results.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shareToken]);

  if (loading) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Card className="max-w-md w-full mx-4">
            <CardContent className="pt-8 pb-8 text-center">
              <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Link Unavailable</h2>
              <p className="text-muted-foreground text-sm">{error}</p>
            </CardContent>
          </Card>
        </div>
      </ThemeProvider>
    );
  }

  const a = data.analysis || {};
  const breakdown = a.question_breakdown || [];
  const violations = data.proctoring_violations || 0;
  const violationLog = data.violation_log || [];
  const aiSummary = data.ai_detection_summary || {};
  const recColor = a.recommendation === 'Strongly Recommend' ? 'bg-green-500' :
                   a.recommendation === 'Recommend' ? 'bg-blue-500' :
                   a.recommendation === 'Consider' ? 'bg-yellow-500' : 'bg-red-500';

  const VIOLATION_LABELS = {
    tab_switch: 'Tab Switch', window_blur: 'Window Focus Lost',
    copy: 'Copy Attempt', paste: 'Paste Attempt', cut: 'Cut Attempt',
    right_click: 'Right Click', keystroke_burst: 'Rapid Typing',
    devtools_open: 'DevTools Detected', ai_content_suspected: 'AI Content Suspected',
  };

  const aiMaxScore = aiSummary.max_score || 0;
  const aiRiskLabel = aiMaxScore >= 60 ? 'High Risk' : aiMaxScore >= 30 ? 'Medium Risk' : 'Low Risk';
  const aiRiskBg = aiMaxScore >= 60 ? 'bg-red-500' : aiMaxScore >= 30 ? 'bg-amber-500' : 'bg-green-500';

  // Integrity Score
  const integrity = data.integrity_score || {};
  const integrityScore = integrity.score ?? 100;
  const integrityLabel = integrity.label || 'Trusted';

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background" data-testid="shared-results-page">
        {/* Shared banner */}
        <div className="bg-indigo-500/10 border-b border-indigo-500/20 py-2 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Share2 className="h-3.5 w-3.5 text-indigo-500" />
              <span className="text-indigo-600 dark:text-indigo-400 font-medium">Shared Interview Results</span>
              <span className="text-muted-foreground">|</span>
              <span className="text-xs text-muted-foreground font-medium">HireFast AI</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{data.views} views</span>
              {data.expires_at && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Expires {new Date(data.expires_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Hero header */}
        <div className="border-b bg-muted/30">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex items-center gap-4">
                <ScoreRing score={a.overall_score || 0} size={130} />
                <div className="flex flex-col items-center gap-1">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center border-2 ${integrityScore >= 80 ? 'border-green-500 text-green-500' : integrityScore >= 50 ? 'border-amber-500 text-amber-500' : 'border-red-500 text-red-500'}`}>
                    <span className="text-lg font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{integrityScore}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground font-medium">Integrity</span>
                  <span className={`text-[9px] ${integrityScore >= 80 ? 'text-green-500' : integrityScore >= 50 ? 'text-amber-500' : 'text-red-500'}`}>{integrityLabel}</span>
                </div>
              </div>
              <div className="flex-1 text-center md:text-left min-w-0">
                <h1 className="text-2xl font-bold truncate" style={{ fontFamily: 'Manrope, sans-serif' }}
                  data-testid="shared-candidate-name">
                  {data.candidate_name || 'Candidate'}
                </h1>
                <p className="text-muted-foreground truncate">{data.job_title}</p>
                <div className="flex flex-wrap items-center gap-2 mt-3 justify-center md:justify-start">
                  <Badge className={`${recColor} text-white px-3 py-1`} data-testid="shared-recommendation">
                    <Award className="h-3.5 w-3.5 mr-1" />
                    {a.recommendation || 'Pending'}
                  </Badge>
                  <Badge variant="outline">{a.fit_assessment || 'Pending'}</Badge>
                  {violations > 0 && (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {violations} Violation{violations > 1 ? 's' : ''}
                    </Badge>
                  )}
                  {aiMaxScore >= 30 && (
                    <Badge className={`${aiRiskBg} text-white gap-1`}>
                      <Shield className="h-3 w-3" />
                      AI: {aiRiskLabel}
                    </Badge>
                  )}
                  {data.attempt_number > 1 && (
                    <Badge variant="secondary">Attempt #{data.attempt_number}</Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-muted-foreground justify-center md:justify-start">
                  {a.total_questions && <span>{a.total_questions} questions answered</span>}
                  {a.total_time_seconds && <span>{Math.round(a.total_time_seconds / 60)} min total</span>}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">
          {/* Scores */}
          <Card data-testid="shared-scores-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <BarChart3 className="h-5 w-5 text-primary" strokeWidth={1.5} />
                Performance Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ScoreBar label="Technical" score={a.technical_score || 0} icon={Zap} />
                <ScoreBar label="Communication" score={a.communication_score || 0} icon={MessageSquare} />
                {a.problem_solving_score > 0 && <ScoreBar label="Problem Solving" score={a.problem_solving_score} icon={TrendingUp} />}
              </div>
            </CardContent>
          </Card>

          {/* Narrative */}
          {a.fit_narrative && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground leading-relaxed italic">{a.fit_narrative}</p>
              </CardContent>
            </Card>
          )}

          {/* Strengths + Improvements */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {a.strengths?.length > 0 && (
              <Card data-testid="shared-strengths-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <TrendingUp className="h-4 w-4 text-green-500" strokeWidth={1.5} />
                    Strengths
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1.5">
                    {a.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-green-500 mt-0.5 shrink-0">+</span>
                        <span className="text-muted-foreground">{s}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            {a.areas_to_improve?.length > 0 && (
              <Card data-testid="shared-improvements-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <XCircle className="h-4 w-4 text-amber-500" strokeWidth={1.5} />
                    Areas to Improve
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1.5">
                    {a.areas_to_improve.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-amber-500 mt-0.5 shrink-0">-</span>
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Violations */}
          {violations > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-destructive" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <AlertTriangle className="h-4 w-4" strokeWidth={1.5} />
                  Proctoring Violations ({violations})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {violationLog.length > 0 ? (
                  <div className="space-y-1.5">
                    {violationLog.map((v, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded-md bg-destructive/5 border border-destructive/10">
                        <AlertTriangle className="h-3 w-3 text-destructive shrink-0" />
                        <Badge variant="destructive" className="text-[10px] h-4">{VIOLATION_LABELS[v.type] || v.type}</Badge>
                        <span className="text-muted-foreground font-mono text-[10px]">
                          {v.timestamp ? new Date(v.timestamp).toLocaleTimeString() : ''}
                        </span>
                        {v.details && <span className="text-muted-foreground truncate flex-1">{v.details}</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{violations} violation(s) recorded during the interview</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* AI Detection */}
          {aiMaxScore > 0 && (
            <Card data-testid="shared-ai-detection-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Shield className="h-4 w-4 text-primary" strokeWidth={1.5} />
                  AI Content Detection
                  <Badge className={`${aiRiskBg} text-white text-xs ml-auto`}>{aiRiskLabel}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-2 bg-muted/30 rounded-lg border">
                    <p className="text-lg font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{aiSummary.max_score || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Peak Score</p>
                  </div>
                  <div className="text-center p-2 bg-muted/30 rounded-lg border">
                    <p className="text-lg font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{aiSummary.avg_score || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Avg Score</p>
                  </div>
                  <div className="text-center p-2 bg-muted/30 rounded-lg border">
                    <p className="text-lg font-bold text-red-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{aiSummary.high_risk_answers || 0}</p>
                    <p className="text-[10px] text-muted-foreground">High Risk</p>
                  </div>
                </div>
                {aiSummary.top_flags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {aiSummary.top_flags.map((f, i) => (
                      <span key={i} className="text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">{f}</span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}


          {/* Interview Integrity Score */}
          {integrity.deductions?.length > 0 && (
            <Card data-testid="shared-integrity-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Shield className="h-4 w-4 text-primary" strokeWidth={1.5} />
                  Interview Integrity Score
                  <Badge
                    className={`ml-auto text-white text-xs ${integrityScore >= 80 ? 'bg-green-500' : integrityScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                  >
                    {integrityScore}/100 — {integrityLabel}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${integrityScore >= 80 ? 'bg-green-500' : integrityScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${integrityScore}%`, transition: 'width 1s ease-out' }}
                  />
                </div>
                <div className="space-y-1 mt-2">
                  {integrity.deductions.map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-1 rounded bg-muted/10 border">
                      <span className="text-muted-foreground">{d.reason}</span>
                      <span className="text-red-400 font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{d.points}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}


          {/* Question Breakdown */}
          <Card data-testid="shared-questions-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <MessageSquare className="h-5 w-5 text-primary" strokeWidth={1.5} />
                Question-by-Question Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="max-h-[600px]">
                <div className="space-y-2">
                  {breakdown.map((q, idx) => (
                    <QuestionCard key={idx} q={q} index={idx} />
                  ))}
                  {breakdown.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No question breakdown available</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Key Points */}
          {a.key_points_covered?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Target className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  Key Points Covered
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {a.key_points_covered.map((p, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{p}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <p className="text-center text-sm text-muted-foreground pb-8">
            Interview completed on {data.completed_at ? new Date(data.completed_at).toLocaleString() : 'N/A'}
          </p>
        </div>
      </div>
    </ThemeProvider>
  );
}
